import { Stack, Text } from '@chakra-ui/react';
import HeaderComponent from '@components/HeaderComponent/HeaderComponent';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BackgroundImage from '@resources/images/customers-banner.jpg';
import FooterComponent from '@components/FooterComponent/FooterComponent';
import { CustomerForm } from 'types/formData';
import { voidCustomerForm } from '@utils/models/CustomerForms';
import Step1 from './steps/Step1';
import { useNavigate, useParams } from 'react-router-dom';
import Step2 from './steps/Step2';
import Step3 from './steps/Step3';
import { SupplierNation } from '@utils/enum/SupplierNation';
import { AppRoutes } from '@routes/appRoutes';
import { StoreContext } from '@contexts/StoreContext';
import { StoreContextType } from 'types/reducers';
import { EntityType } from 'types/data';
import { DEFAULT_EXTRA_UE_VAT } from '@utils/constants';

const NewCustomerPage: FC = () => {
    const params = useParams();
    const [step, setStep] = useState(1);
    const [formDataCustomer, setFormDataCustomer] = useState<CustomerForm>(voidCustomerForm);
    const { state } = useContext<StoreContextType>(StoreContext);
    const navigate = useNavigate();

    useEffect(() => {
        const type = params.type;
        if (type) {
            setFormDataCustomer({
                ...formDataCustomer,
                supplierNation: type,
                countryTax: type === 'italy' ? 'IT' : '',
                vat: type === SupplierNation.EXTRA_EU_COUNTRY ? DEFAULT_EXTRA_UE_VAT : '',
                entityType: state.entityTypes.find((et: EntityType) => et.tag === 'CUSTOMER')?.id ?? -1,
                legalAddress: {
                    ...formDataCustomer.legalAddress,
                    country: type === 'italy' ? 'IT' : '',
                },
            });
        } else {
            navigate(AppRoutes.HOME_PAGE);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.type, navigate, state.entityTypes]);

    const handleNextStep = useCallback(
        (form: CustomerForm) => {
            setFormDataCustomer(form);
            setStep(step + 1);
        },
        [step],
    );

    const handlePreviousStep = useCallback(() => {
        setStep(step - 1);
    }, [step]);

    const handleSendForm = useCallback(() => {}, [step]);

    const renderStep = useMemo(() => {
        if (formDataCustomer.supplierNation as SupplierNation) {
            switch (step) {
                case 1:
                    return (
                        <Step1
                            customerForm={formDataCustomer}
                            nextStep={handleNextStep}
                            supplierNation={formDataCustomer.supplierNation as SupplierNation}
                        />
                    );
                case 2:
                    return (
                        <Step2
                            customerForm={formDataCustomer}
                            nextStep={handleNextStep}
                            previousStep={handlePreviousStep}
                            supplierNation={formDataCustomer.supplierNation as SupplierNation}
                        />
                    );
                case 3:
                    return (
                        <Step3
                            customerForm={formDataCustomer}
                            endForm={handleSendForm}
                            previousStep={handlePreviousStep}
                        />
                    );
                default:
                    return (
                        <Step1
                            customerForm={formDataCustomer}
                            nextStep={handleNextStep}
                            supplierNation={formDataCustomer.supplierNation as SupplierNation}
                        />
                    );
            }
        }
    }, [formDataCustomer.supplierNation, step, formDataCustomer, handleNextStep, handlePreviousStep]);

    const titlePage = useMemo(() => {
        // console.log(formDataCustomer.supplierNation);
        switch (formDataCustomer.supplierNation) {
            case SupplierNation.ITALY:
                return 'Nuovo cliente Italia';
            case SupplierNation.EU_COUNTRY:
                return 'Nuovo cliente UE';
            case SupplierNation.EXTRA_EU_COUNTRY:
                return 'Nuovo cliente Extra UE';
            default:
                return 'Nuovo cliente';
        }
    }, [formDataCustomer.supplierNation]);

    return (
        <Stack minHeight="100vh" backgroundColor="white">
            <HeaderComponent title={titlePage} goBack backgroundImg={BackgroundImage} />
            {formDataCustomer ? (
                <>
                    <Text variant="text" align="center" marginTop="30px" color="gray.600">
                        step {step} di 3
                    </Text>
                    {renderStep}
                </>
            ) : (
                <Stack flex={1} />
            )}
            <FooterComponent />
        </Stack>
    );
};

export default NewCustomerPage;
